import { Forecast, ForecastDataPoint } from "../types";

/// Climbing conditions rating: turns weather into a 1–5 score and a label.
///
/// The score starts at 5 and loses points for each factor that makes climbing
/// worse. Thresholds live in CONDITIONS_THRESHOLDS so they can be tuned in one place.

export type ConditionsScore = 1 | 2 | 3 | 4 | 5;

export type ConditionsLabel = "Prime" | "Good" | "Fair" | "Iffy" | "Skip";

export type ConditionsFactor = "rain" | "wet" | "damp" | "cold" | "hot" | "wind" | "muggy";

/// Weather inputs in display units. Missing values are ignored rather than penalized.
export interface Conditions {
    temperature?: number;        // °F, feels-like when available
    precipProbability?: number;  // 0–1; for a whole day, the daytime chance
    precipAccumulation?: number; // inches; for a whole day, daytime rain only
    precipProbabilityNight?: number;  // 0–1, a day's overnight chance (shown, not scored)
    precipAccumulationNight?: number; // inches, a day's overnight rain (shown, not scored)
    overnightRain?: number;      // inches that fell the night before, which can leave rock damp
    windSpeed?: number;          // mph, sustained
    windGust?: number;           // mph
    windBearing?: number;        // degrees the wind comes from (0 = north); shown, not scored
    humidity?: number;           // 0–1, shown for context; mugginess comes from the dew point
    dewPoint?: number;           // °F
}

export interface ConditionsRating {
    score: ConditionsScore;
    label: ConditionsLabel;
    /// Factors that lowered the score, largest penalty first
    factors: ConditionsFactor[];
}

export const CONDITIONS_THRESHOLDS = {
    // Comfortable climbing range; penalty grows outside it
    idealTempLow: 45,
    idealTempHigh: 75,
    coldDegreesPerPoint: 10,
    hotDegreesPerPoint: 6,
    // [minimum probability, penalty], checked from the top
    precipProbability: [[0.8, 4], [0.6, 3], [0.4, 2], [0.2, 1]] as [number, number][],
    // Measurable rain costs points; soaking rain means the rock is wet regardless
    precipAccumulationPenalty: 0.05,
    precipAccumulationWet: 0.25,
    // Rain the night before leaves rock damp in the morning, or wet for longer
    overnightRain: [[0.25, 2], [0.05, 1]] as [number, number][],
    // Hours of the next morning that count as damp, from when the forecast's day starts (5 AM)
    dampMorningHours: 6,
    windSpeed: [[25, 2], [15, 1]] as [number, number][],
    windGust: [[40, 2], [30, 1]] as [number, number][],
    // Dew point °F, which tracks how sticky the air feels at any temperature (relative
    // humidity runs high on cool, dry-feeling days): a bit sticky at 55, muggy at 60
    dewPoint: [[65, 1.5], [60, 1], [55, 0.5]] as [number, number][],
    // A factor is reported once it costs at least this much
    factorMinPenalty: 0.5,
};

const LABELS: Record<ConditionsScore, ConditionsLabel> = {
    5: "Prime",
    4: "Good",
    3: "Fair",
    2: "Iffy",
    1: "Skip",
};

export function conditionsLabel(score: ConditionsScore): ConditionsLabel {
    return LABELS[score];
}

function stepPenalty(value: number | undefined, steps: [number, number][]): number {
    if (value === undefined) {
        return 0;
    }
    const step = steps.find(([min]) => value >= min);
    return step ? step[1] : 0;
}

function clampScore(value: number): ConditionsScore {
    return Math.min(5, Math.max(1, Math.round(value))) as ConditionsScore;
}

/// Points each factor costs; shared by the score and its explanation
function penaltiesFor(c: Conditions): { penalties: [ConditionsFactor, number][]; wet: boolean } {
    const t = CONDITIONS_THRESHOLDS;
    const penalties: [ConditionsFactor, number][] = [];

    if (c.temperature !== undefined) {
        if (c.temperature < t.idealTempLow) {
            penalties.push(["cold", (t.idealTempLow - c.temperature) / t.coldDegreesPerPoint]);
        } else if (c.temperature > t.idealTempHigh) {
            penalties.push(["hot", (c.temperature - t.idealTempHigh) / t.hotDegreesPerPoint]);
        }
    }

    const wet = (c.precipAccumulation ?? 0) >= t.precipAccumulationWet;
    const accumulationPenalty = (c.precipAccumulation ?? 0) >= t.precipAccumulationPenalty ? 2 : 0;
    const rainPenalty = Math.max(stepPenalty(c.precipProbability, t.precipProbability), accumulationPenalty);
    // Wet rock outranks everything else, so it is listed first
    penalties.push(wet ? ["wet", 5] : ["rain", rainPenalty]);

    penalties.push(["damp", stepPenalty(c.overnightRain, t.overnightRain)]);

    penalties.push(["wind", Math.max(stepPenalty(c.windSpeed, t.windSpeed), stepPenalty(c.windGust, t.windGust))]);
    penalties.push(["muggy", stepPenalty(c.dewPoint, t.dewPoint)]);

    return { penalties, wet };
}

export function rateConditions(c: Conditions): ConditionsRating {
    const t = CONDITIONS_THRESHOLDS;
    const { penalties, wet } = penaltiesFor(c);

    const total = penalties.reduce((sum, [, p]) => sum + p, 0);
    const score = wet ? 1 : clampScore(5 - total);

    // A factor counts only if it actually lowered the rating: without it the score would
    // be higher. So a Prime day never lists a problem, and wet rock stands alone.
    const significant = penalties.filter(([, p]) => p >= t.factorMinPenalty);
    const lowered = significant.filter(([factor, p]) =>
        factor === "wet" || (!wet && clampScore(5 - (total - p)) > score));
    // Rarely, several issues lower a score together without any one doing it alone
    const counted = lowered.length > 0 || score === 5 ? lowered : significant;

    const factors = counted
        .sort((a, b) => b[1] - a[1])
        .map(([factor]) => factor);

    return { score, label: conditionsLabel(score), factors };
}

export type FactorKey = "temperature" | "rain" | "nightBefore" | "wind" | "dewPoint" | "humidity";

/// One input to a rating, worded for people: "Wind: 18 mph, gusts to 32"
export interface FactorDetail {
    key: FactorKey;
    label: string;
    value: string;
    /// True when this factor lowered the rating
    issue: boolean;
}

/// What went into a rating, factor by factor. Missing inputs are left out.
/// `temperatureLabel` is "High" for a whole day.
export function explainConditions(c: Conditions, temperatureLabel = "Temperature"): FactorDetail[] {
    const t = CONDITIONS_THRESHOLDS;
    const flagged = new Set(rateConditions(c).factors);
    const details: FactorDetail[] = [];

    if (c.temperature !== undefined) {
        const cold = flagged.has("cold"), hot = flagged.has("hot");
        const word = cold ? "cold"
            : hot ? "hot"
            : c.temperature < t.idealTempLow ? "a little cool"
            : c.temperature > t.idealTempHigh ? "a little warm"
            : "comfortable";
        details.push({ key: "temperature", label: temperatureLabel, value: `${Math.round(c.temperature)}°, ${word}`, issue: cold || hot });
    }

    if (c.precipProbability !== undefined || c.precipAccumulation !== undefined) {
        // A whole day shows daytime and overnight separately, so a dry day before a wet
        // night reads "1% daytime, 52% overnight" rather than a puzzling "1%, about 0.06\""
        const split = c.precipProbabilityNight !== undefined;
        const parts: string[] = [];
        const amount = (inches: number | undefined, when: string) => {
            if ((inches ?? 0) >= 0.01) {
                parts.push(`about ${inches!.toFixed(2)}"${when}`);
            }
        };
        if (c.precipProbability !== undefined) {
            parts.push(`${Math.round(c.precipProbability * 100)}%${split ? " daytime" : " chance"}`);
        }
        amount(c.precipAccumulation, split ? " daytime" : "");
        if (split) {
            parts.push(`${Math.round(c.precipProbabilityNight! * 100)}% overnight`);
            amount(c.precipAccumulationNight, " overnight");
        }
        if (flagged.has("wet")) {
            parts.push("wet rock");
        }
        details.push({ key: "rain", label: "Rain", value: parts.join(", "), issue: flagged.has("rain") || flagged.has("wet") });
    }

    if ((c.overnightRain ?? 0) >= 0.01) {
        const damp = flagged.has("damp");
        details.push({
            key: "nightBefore", label: "Night before",
            value: `about ${c.overnightRain!.toFixed(2)}" of rain${damp ? ", damp rock early" : ""}`,
            issue: damp,
        });
    }

    if (c.windSpeed !== undefined || c.windGust !== undefined) {
        const parts: string[] = [];
        if (c.windSpeed !== undefined) {
            const calm = Math.round(c.windSpeed) === 0;
            parts.push(`${Math.round(c.windSpeed)} mph` + (c.windBearing !== undefined && !calm ? ` from the ${compassDirection(c.windBearing)}` : ""));
        }
        if (c.windGust !== undefined && (c.windSpeed === undefined || c.windGust > c.windSpeed)) {
            parts.push(`gusts to ${Math.round(c.windGust)}`);
        }
        details.push({ key: "wind", label: "Wind", value: parts.join(", "), issue: flagged.has("wind") });
    }

    if (c.dewPoint !== undefined) {
        const word = c.dewPoint >= 65 ? "very muggy"
            : c.dewPoint >= 60 ? "muggy"
            : c.dewPoint >= 55 ? "a bit sticky"
            : "comfortable";
        const humidity = c.humidity !== undefined ? ` (${Math.round(c.humidity * 100)}% humidity)` : "";
        details.push({ key: "dewPoint", label: "Dew point", value: `${Math.round(c.dewPoint)}°, ${word}${humidity}`, issue: flagged.has("muggy") });
    } else if (c.humidity !== undefined) {
        details.push({ key: "humidity", label: "Humidity", value: `${Math.round(c.humidity * 100)}%`, issue: false });
    }

    return details;
}

/// Parse a forecast value. The API sends some numeric fields as numbers even though
/// ForecastDataPoint types them as strings, so both are accepted.
export function parseNumber(value: string | number | null | undefined): number | undefined {
    if (value === undefined || value === null) {
        return undefined;
    }
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : undefined;
    }
    if (value.trim() === "") {
        return undefined;
    }
    const n = parseFloat(value);
    return Number.isNaN(n) ? undefined : n;
}

const COMPASS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];

/// Eight-point compass direction for a bearing in degrees: 225 → "SW"
export function compassDirection(bearing: number): string {
    const normalized = ((bearing % 360) + 360) % 360;
    return COMPASS[Math.round(normalized / 45) % 8];
}

/// Dew point in °F from air temperature (°F) and relative humidity (0–1), Magnus formula
export function dewPointF(temperature: number, humidity: number): number {
    const c = (temperature - 32) * 5 / 9;
    const a = 17.62, b = 243.12;
    const g = Math.log(Math.max(humidity, 0.01)) + (a * c) / (b + c);
    return ((b * g) / (a - g)) * 9 / 5 + 32;
}

function dewPointFrom(temperature: number | undefined, humidity: number | undefined): number | undefined {
    return temperature !== undefined && humidity !== undefined ? dewPointF(temperature, humidity) : undefined;
}

/// Rating inputs for one hour of the hourly forecast. `overnightRain` is the rain
/// from the night before, for morning hours that may still have damp rock.
export function hourConditions(dp: ForecastDataPoint, overnightRain?: number): Conditions {
    const humidity = parseNumber(dp.humidity);
    return {
        overnightRain,
        temperature: parseNumber(dp.apparentTemperature) ?? parseNumber(dp.temperature),
        precipProbability: parseNumber(dp.precipProbability),
        precipAccumulation: parseNumber(dp.precipAccumulation),
        windSpeed: parseNumber(dp.windSpeed),
        windGust: parseNumber(dp.windGust),
        windBearing: parseNumber(dp.windBearing),
        humidity,
        dewPoint: parseNumber(dp.dewPoint) ?? dewPointFrom(parseNumber(dp.temperature), humidity),
    };
}

const HALF_DAY = 12 * 3600;

/// A day's rain split into its daytime (first 12 hours) and overnight parts. The daily
/// total covers both, so a dry day before a rainy night would otherwise look wet.
/// Uses the hourly amounts when they cover the whole daytime period; otherwise splits
/// the total in proportion to the daytime and overnight chances.
export function splitDayRain(day: ForecastDataPoint, hourly: ForecastDataPoint[] = []): { day?: number; night?: number } {
    const total = parseNumber(day.precipAccumulation);
    if (total === undefined) {
        return {};
    }
    const start = Number(day.time);
    const mid = start + HALF_DAY;
    const covered = hourly.length > 0 && Number(hourly[0].time) <= start && Number(hourly[hourly.length - 1].time) >= mid - 3600;
    if (covered) {
        const daytime = hourly
            .filter((h) => Number(h.time) >= start && Number(h.time) < mid)
            .reduce((sum, h) => sum + (parseNumber(h.precipAccumulation) ?? 0), 0);
        return { day: Math.min(daytime, total), night: Math.max(total - daytime, 0) };
    }
    const dayChance = parseNumber(day.precipProbability) ?? 0;
    const nightChance = parseNumber(day.precipProbabilityNight);
    if (nightChance === undefined) {
        return { day: total, night: 0 };
    }
    const share = dayChance + nightChance > 0 ? dayChance / (dayChance + nightChance) : 0.5;
    return { day: total * share, night: total * (1 - share) };
}

/// What else rating a day needs from the rest of the forecast
export interface DayContext {
    /// The hourly forecast, to split the day's rain between daytime and overnight
    hourly?: ForecastDataPoint[];
    /// The day before, whose overnight rain can leave rock damp
    previousDay?: ForecastDataPoint;
}

/// Rating inputs for a whole day, using the daytime high for temperature and daytime
/// rain. The day's humidity is an average, so its dew point uses the day's average
/// temperature; pairing it with the high would overstate how muggy the day is.
export function dayConditions(dp: ForecastDataPoint, context: DayContext = {}): Conditions {
    const humidity = parseNumber(dp.humidity);
    const high = parseNumber(dp.temperatureHigh);
    const low = parseNumber(dp.temperatureLow);
    const average = high !== undefined && low !== undefined ? (high + low) / 2 : high;
    const rain = splitDayRain(dp, context.hourly);
    return {
        temperature: parseNumber(dp.apparentTemperatureHigh) ?? high,
        precipProbability: parseNumber(dp.precipProbability),
        precipAccumulation: rain.day,
        precipProbabilityNight: parseNumber(dp.precipProbabilityNight),
        precipAccumulationNight: rain.night,
        overnightRain: context.previousDay ? splitDayRain(context.previousDay, context.hourly).night : undefined,
        windSpeed: parseNumber(dp.windSpeed),
        windGust: parseNumber(dp.windGust),
        humidity,
        dewPoint: parseNumber(dp.dewPoint) ?? dewPointFrom(average, humidity),
    };
}

/// Rating inputs for day `index` of a forecast, with its hourly data and the day before
export function forecastDayConditions(forecast: Forecast, index: number): Conditions {
    const days = forecast.daily?.data ?? [];
    return dayConditions(days[index], { hourly: forecast.hourly?.data, previousDay: days[index - 1] });
}

/// Rate one hour of the hourly forecast
export function rateHour(dp: ForecastDataPoint): ConditionsRating {
    return rateConditions(hourConditions(dp));
}

/// Rating inputs for an hour of a forecast. Early-morning hours carry the previous
/// night's rain, so they rate the same way the day does ("damp rock early").
export function forecastHourConditions(forecast: Forecast, dp: ForecastDataPoint): Conditions {
    const days = forecast.daily?.data ?? [];
    const time = Number(dp.time);
    const morning = CONDITIONS_THRESHOLDS.dampMorningHours * 3600;
    const index = days.findIndex((d) => time >= Number(d.time) && time < Number(d.time) + morning);
    const overnightRain = index > 0 ? splitDayRain(days[index - 1], forecast.hourly?.data).night : undefined;
    return hourConditions(dp, overnightRain);
}

/// Rate an hour of a forecast, including damp rock after a rainy night
export function rateForecastHour(forecast: Forecast, dp: ForecastDataPoint): ConditionsRating {
    return rateConditions(forecastHourConditions(forecast, dp));
}

/// Rate a whole day of the daily forecast
export function rateDay(dp: ForecastDataPoint, context: DayContext = {}): ConditionsRating {
    return rateConditions(dayConditions(dp, context));
}

/// Rate day `index` of a forecast, with its hourly data and the day before
export function rateForecastDay(forecast: Forecast, index: number): ConditionsRating {
    return rateConditions(forecastDayConditions(forecast, index));
}

export interface ClimbingWindow {
    start: number;  // unix seconds, start of first hour
    end: number;    // unix seconds, end of last hour
    hours: number;
    score: ConditionsScore; // lowest score inside the window
}

export interface BestWindowOptions {
    /// Lowest score worth calling a window (default 4, "Good")
    minScore?: ConditionsScore;
    /// Shortest window worth calling out (default 2 hours)
    minHours?: number;
    /// Only count hours between sunrise and sunset (unix seconds)
    daylight?: { sunrise: number; sunset: number };
    /// How to rate an hour (default rateHour); pass rateForecastHour to include damp mornings
    rate?: (dp: ForecastDataPoint) => ConditionsRating;
}

const HOUR = 3600;

/// Find the best stretch of consecutive hours to climb.
/// Prefers the highest score that holds for at least minHours, then the longest
/// such stretch, then the earliest.
export function bestWindow(hours: ForecastDataPoint[], options: BestWindowOptions = {}): ClimbingWindow | null {
    const minScore = options.minScore ?? 4;
    const minHours = options.minHours ?? 2;
    const daylight = options.daylight;
    const rate = options.rate ?? rateHour;

    const rated = hours.map((dp) => {
        const inDaylight = !daylight || (dp.time >= daylight.sunrise && dp.time < daylight.sunset);
        return { time: dp.time, score: inDaylight ? rate(dp).score : 0 };
    });

    for (let target = 5; target >= minScore; target--) {
        let best: { from: number; to: number } | null = null;
        let runStart = -1;

        for (let i = 0; i <= rated.length; i++) {
            const ok = i < rated.length && rated[i].score >= target;
            const contiguous = ok && runStart >= 0 && rated[i].time - rated[i - 1].time <= HOUR;

            if (ok && runStart >= 0 && !contiguous) {
                // Gap in the data ends the run; the current hour starts a new one
                best = longer(best, runStart, i - 1);
                runStart = i;
            } else if (ok && runStart < 0) {
                runStart = i;
            } else if (!ok && runStart >= 0) {
                best = longer(best, runStart, i - 1);
                runStart = -1;
            }
        }

        if (best && best.to - best.from + 1 >= minHours) {
            const window = rated.slice(best.from, best.to + 1);
            return {
                start: window[0].time,
                end: window[window.length - 1].time + HOUR,
                hours: window.length,
                score: Math.min(...window.map((h) => h.score)) as ConditionsScore,
            };
        }
    }

    return null;
}

function longer(best: { from: number; to: number } | null, from: number, to: number) {
    if (best === null || to - from > best.to - best.from) {
        return { from, to };
    }
    return best;
}
